import type { Object3D } from 'three';

/** Gniazdo na sprzęt: punkt przypięty do kości modelu, z przesunięciem i obrotem (w stopniach). */
export interface Socket {
  bone: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: number;
  /** Dodatkowy obrót broni do walki wręcz w postawie gotowości (domyślnie UAL_READY). */
  ready?: [number, number, number];
}

/**
 * Profil szkieletu innego niż KayKit: gra używa nazw animacji i kości KayKit (Idle, Running_A, handslotr…),
 * a profil tłumaczy je na to, co ma model. Dzięki temu postacie na innym szkielecie działają bez zmian w grze.
 */
export interface RigProfile {
  /** Skala modelu względem postaci KayKit (żeby bohater miał podobną wielkość na scenie). */
  scale: number;
  /**
   * Prędkość stóp w klipie biegu przy tempie 1 (jednostki sceny/s, już ze skalą modelu) — tempo biegu
   * dopasowuje się do prędkości postaci, żeby stopy nie ślizgały się po ziemi.
   */
  runSpeed: number;
  /** nazwa animacji używana w grze → nazwa klipu w modelu */
  anims: Record<string, string>;
  /** gniazda sprzętu używane w grze (handslotr, handslotl, head, chest) → kość modelu */
  sockets: Record<string, Socket>;
  /** Czy ścieżka animacji należy do górnej połowy ciała (atak tułowiem, nogi dalej biegną). */
  isUpper(node: string): boolean;
  /** Kości lewej ręki — atak bronią jednoręczną ich nie rusza (tarcza nie fruwa nad głową przy cięciu). */
  isLeftArm?(node: string): boolean;
  /** Kości prawej ręki — w biegu z bronią w gotowości ręka gra osobną postawę (Armed_Idle). */
  isRightArm?(node: string): boolean;
  /** Kości ramion i dłoni [lewa, prawa] — do poprawki pozy masywnych postaci (PoseFix). */
  arms: { upper: [string, string]; hand: [string, string] };
}

/**
 * Poprawka pozy dla modelu szerszego niż szkielet szablonu (np. zbroja z AI na smukłym manekinie UAL):
 * bez niej ramiona opuszczone w animacjach chowają się w tułowiu i widać tylko dłonie.
 */
export interface PoseFix {
  /** O ile stopni odchylić ramiona na boki (w płaszczyźnie przodu postaci), w każdej klatce animacji. */
  armSpread: number;
  /** Skala dłoni (dłonie z AI bywają za duże); sprzęt w dłoniach zachowuje swój rozmiar. */
  handScale?: number;
}

/** Poprawki pozy dla plików modeli (ścieżka jak w HERO_MODELS). */
export const POSE_FIXES: Record<string, PoseFix> = {
  'characters/czlowiek-m.glb': { armSpread: 15, handScale: 0.8 },
};

/**
 * Gniazda sprzętu dopasowane do konkretnego ciała (ścieżka jak w HERO_MODELS). Smukły manekin ma mniejszą głowę
 * i dłonie niż masywna postać z AI, pod którą dobrano UAL_RIG — bez tego hełmy i broń KayKit są dwa razy za duże.
 */
export const BODY_SOCKETS = 'characters/cialo-ual.glb';
export const SOCKET_FIXES: Record<string, Record<string, Partial<Socket>>> = {
  [BODY_SOCKETS]: {
    handslotr: { position: [0, 0.07, 0.02], scale: 0.6 },
    handslotl: { position: [0, 0.07, 0.02], scale: 0.6 },
    head: { position: [0, 0.03, 0], scale: 0.3 },
    chest: { position: [0, -0.06, -0.01], scale: 0.52 },
  },
};

/**
 * Ciała ras z AI: palce zaciśnięte animacją (finger-weights.ts), więc uchwyt broni idzie w środek pięści
 * (średnia kości palców), a nie przy nadgarstku — inaczej głowica wystawała z grzbietu dłoni.
 */
export const RACE_SOCKETS: Record<string, Partial<Socket>> = {
  ...SOCKET_FIXES[BODY_SOCKETS],
  // Kość głowy ciał ras (Universal Base Characters) jest u nasady czaszki — nakrycia głowy wyżej i trochę większe niż na manekinie.
  head: { position: [0, 0.19, 0], scale: 0.36 },
  // Płaszcze: ciało Superhero ma szersze barki niż manekin — większe i dosunięte do pleców, od linii barków.
  chest: { position: [0, -0.12, 0.22], scale: 0.95 },
  // Chwyt wyliczony z dłoni ciała ras: rękojeść wzdłuż linii knykci przez środek pięści (średnia kości zgiętych palców),
  // ostrze ok. 30° nad tą linią — wcześniej rękojeść szła na ukos przez grzbiet dłoni. Obrót gotowości już w gnieździe.
  handslotr: { position: [-0.047, 0.09, 0.031], rotation: [120, 90, 0], scale: 0.6, ready: [0, 0, 0] },
};

/**
 * Chwyt broni innej niż miecz na ciele UAL (dłoń opuszczona, palce w dół): obrót w stopniach dodany do gniazda
 * i przesunięcie wzdłuż broni (część długości, o którą broń wysuwa się „za” dłoń). Kostur i różdżkę trzyma się
 * głowicą w górę, kuszę łukiem wzdłuż palców (opuszczona w spoczynku, do przodu przy strzale) — z samym gniazdem miecza kostur wbijał się głowicą w ziemię.
 */
export interface Hold {
  test: RegExp;
  rotation: [number, number, number];
  /** Przesunięcie wzdłuż osi broni (w jednostkach modelu broni). */
  slide?: number;
}
// Tylko dodatki KayKit ("mage:2H_Staff", "rogue:1H_Crossbow"): przedmioty z potoku AI ("item:…") mają własne
// ułożenie w item-models.json (głowicą w stronę palców, jak miecz) i odwrócenie stawiało je głowicą w dół.
/**
 * Broń do walki wręcz z potoku AI (miecz, topór, klinga, sztylet) w postawie gotowości: ostrzem w górę.
 * Obrót względem gniazda (-50°) daje 135° — ostrze w górę i lekko do przodu przy Sword_Idle i w zamachu.
 * Kusza, różdżka i kostur zostają w zwykłym chwycie.
 */
export const UAL_READY = { test: /^item:(?!.*(kusza|rozdzka|kostur))/, rotation: [185, 0, 0] as [number, number, number] };

export const UAL_HOLDS: Hold[] = [
  { test: /^[a-z-]+:.*(Staff|Wand)/, rotation: [180, 0, 0], slide: -0.35 },
  { test: /^[a-z-]+:.*Crossbow/, rotation: [90, 0, 90] },
];

const sword = 'Sword_Attack';

/** Universal Animation Library (Quaternius, CC0): szkielet o ludzkich proporcjach, kości „DEF-…”. */
export const UAL_RIG: RigProfile = {
  scale: 1.2,
  // Jog_Fwd_Loop: stopa w fazie podparcia cofa się ok. 4,3 j/s względem bioder (pomiar klipu) × skala 1,2.
  runSpeed: 5.2,
  anims: {
    Idle: 'Idle_Loop',
    '2H_Melee_Idle': 'Idle_Loop',
    // Postawa z bronią w gotowości (ostrze w górze) — spoczynek i prawa ręka w biegu, gdy bohater trzyma broń.
    Armed_Idle: 'Sword_Idle',
    Running_A: 'Jog_Fwd_Loop',
    Running_B: 'Sprint_Loop',
    Running_C: 'Sprint_Loop',
    Walking_A: 'Walk_Loop',
    Death_A: 'Death01',
    Hit_A: 'Hit_Chest',
    Hit_B: 'Hit_Head',
    Cheer: 'Interact',
    '1H_Melee_Attack_Slice_Horizontal': sword,
    '1H_Melee_Attack_Slice_Diagonal': sword,
    '1H_Melee_Attack_Chop': sword,
    '1H_Melee_Attack_Stab': sword,
    '2H_Melee_Attack_Slice': sword,
    '2H_Melee_Attack_Chop': sword,
    '2H_Melee_Attack_Spinning': sword,
    Spellcast_Shoot: 'Spell_Simple_Shoot',
    Spellcast_Raise: 'Spell_Simple_Shoot',
    '1H_Ranged_Shoot': 'Pistol_Shoot',
    '2H_Ranged_Shoot': 'Pistol_Shoot',
    Throw: 'Punch_Jab',
  },
  sockets: {
    // Broń KayKit jest modelowana „w górę” od uchwytu; dłoń UAL ma oś Y wzdłuż palców — obrót ustawia ostrze
    // skośnie w dół i do przodu przy opuszczonej dłoni (przy -90° chowało się za plecami).
    handslotr: { bone: 'DEF-handR', position: [0, 0.08, 0.02], rotation: [-50, 0, 0], scale: 0.85 },
    // Tarcza obrócona licem na zewnątrz (z obrotem jak w prawej dłoni stała bokiem do kamery).
    handslotl: { bone: 'DEF-handL', position: [0, 0.08, 0.02], rotation: [-90, -90, 0], scale: 0.85 },
    head: { bone: 'DEF-head', position: [0, 0.02, 0], rotation: [0, 0, 0], scale: 0.48 },
    chest: { bone: 'DEF-spine003', position: [0, -0.1, 0], rotation: [0, 0, 0], scale: 0.72 },
  },
  isUpper: (node) => !/^(root|DEF-hips|DEF-thigh|DEF-shin|DEF-foot|DEF-toe)/.test(node),
  isLeftArm: (node) => /^DEF-(shoulder|upper_arm|forearm|hand|f_|thumb).*L$/.test(node),
  isRightArm: (node) => /^DEF-(shoulder|upper_arm|forearm|hand|f_|thumb).*R$/.test(node),
  arms: { upper: ['DEF-upper_armL', 'DEF-upper_armR'], hand: ['DEF-handL', 'DEF-handR'] },
};

/** Prędkość stóp w biegu postaci KayKit (Running_A) przy tempie 1. */
export const KAYKIT_RUN_SPEED = 4;

/** Profil dla szablonu modelu albo null (KayKit — nazwy gry pasują wprost). */
export function rigFor(scene: Object3D): RigProfile | null {
  let ual = false;
  scene.traverse((o) => {
    if (o.name === 'DEF-hips') ual = true;
  });
  return ual ? UAL_RIG : null;
}
